/* PrimeCut: intentional size choice for Kaching deals on the single-tee page.
   Kaching keeps its preselected deal, prices, colors and free gift. This script only makes size a customer decision:
   - every visible Size select in the Kaching block starts on "Select size" instead of Kaching's first size (XXS);
   - Add to Cart is stopped with an inline message until every tee slot in the selected deal has a chosen size;
   - Kaching's cart request is compared with the chosen color and size of each slot and refused before it reaches
     the cart if anything differs, so a stale selection can never add a size the customer did not pick. */
(() => {
  'use strict';

  const start = (block) => {
  const scope = block.closest('[data-pc-pdp]') || document;
  const PLACEHOLDER = 'Select size';
  const MISSING = 'Choose a size for each tee before adding to cart.';
  const MISMATCH = 'Your size selection changed. Check each size and try again.';
  const CHOOSE_ONE = 'Choose a size before adding to cart.';

  let variants = [];
  try {
    variants = JSON.parse(scope.querySelector('[data-pc-product-json]').textContent).variants || [];
  } catch (e) {
    variants = [];
  }
  const variantIds = new Set(variants.map((v) => String(v.id)));
  const chosen = new Map(); // `${deal}:${slot}` -> size the customer picked

  const style = document.createElement('style');
  style.textContent = `
    kaching-bundle select.pc-size-missing { outline: 2px solid #b3261e; outline-offset: 1px; }
    .pc-kaching-size-error { margin: 10px 0 0; padding: 10px 12px; border: 1px solid #b3261e; background: #fdf1f0;
      color: #8c1d18; font-size: 14px; font-weight: 600; line-height: 1.4; }
    .pc-kaching-size-error[hidden] { display: none; }`;
  document.head.appendChild(style);

  const isVisible = (el) => !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  const dealId = () => {
    const checked = block.querySelector('input[type="radio"]:checked');
    return checked ? checked.value : 'deal';
  };
  const slots = () =>
    [...block.querySelectorAll('.kaching-bundles__bar-variant-selects')]
      .filter(isVisible)
      .map((wrap, index) => ({
        key: `${dealId()}:${index}`,
        size: wrap.querySelector('select[data-name="Size"]'),
        color: wrap.querySelector('select[data-name="Color"]'),
      }))
      .filter((slot) => slot.size);
  const isChosen = (slot) => !!chosen.get(slot.key) && slot.size.value === chosen.get(slot.key);
  const missing = () => slots().filter((slot) => !isChosen(slot));
  // Some Kaching renders (for example the app embed on a fallback product template) show deal bars without per-tee
  // size menus and use the theme form's variant for every tee. Then the size comes from the theme picker.
  const hasDeals = () => !!block.querySelector('.kaching-bundles__bar');
  const themeForm = () => scope.querySelector('form[data-type="add-to-cart-form"]');
  // The form's hidden id starts on the first variant (XXS), so a size only counts once it is checked in the theme
  // picker and the form's variant carries that size.
  const themeVariantId = () => {
    const input = themeForm() && themeForm().querySelector('[name="id"]');
    if (!input || input.disabled || !input.value) return null;
    const sizeField = scope.querySelector('[data-pc-size-option]');
    if (sizeField) {
      const picked = sizeField.querySelector('input:checked');
      const variant = variants.find((v) => String(v.id) === String(input.value));
      if (!picked || !variant || !variant.options.includes(picked.value)) return null;
    }
    return String(input.value);
  };
  const themeSizeMissing = () => hasDeals() && slots().length === 0 && !themeVariantId();

  const message = document.createElement('p');
  message.className = 'pc-kaching-size-error';
  message.setAttribute('role', 'alert');
  message.hidden = true;
  const showMessage = (text) => {
    if (!message.isConnected) block.insertAdjacentElement('afterend', message);
    message.textContent = text;
    message.hidden = false;
  };
  const clearMessage = () => {
    message.hidden = true;
    message.textContent = '';
  };
  const markMissing = (gaps) =>
    gaps.forEach((slot) => {
      slot.size.setAttribute('aria-invalid', 'true');
      slot.size.classList.add('pc-size-missing');
    });

  function prepare(slot) {
    const select = slot.size;
    if (!select.querySelector('option[data-pc-placeholder]')) {
      const option = document.createElement('option');
      option.value = '';
      option.textContent = PLACEHOLDER;
      option.disabled = true;
      option.dataset.pcPlaceholder = '';
      select.prepend(option);
    }
    const picked = chosen.get(slot.key);
    if (picked && select.value !== picked) {
      // Kaching re-rendered this slot with its own default: put the customer's choice back, in Kaching too.
      select.value = picked;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (!picked && select.value !== '') {
      select.value = '';
    }
    if (select.dataset.pcSizeReady) return;
    select.dataset.pcSizeReady = 'true';
    select.addEventListener('change', () => {
      if (!select.value) return;
      const current = slots().find((s) => s.size === select);
      if (current) chosen.set(current.key, select.value);
      select.removeAttribute('aria-invalid');
      select.classList.remove('pc-size-missing');
      if (!missing().length) clearMessage();
    });
  }

  let queued = false;
  const refresh = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      slots().forEach(prepare);
    });
  };
  new MutationObserver(refresh).observe(block, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'style', 'hidden'],
  });
  block.addEventListener('click', () => setTimeout(refresh, 60));
  refresh();
  window.addEventListener('load', refresh);

  // Stop the add before Kaching or the theme form handles it.
  const stopIfMissing = (event) => {
    if (themeSizeMissing()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      showMessage(CHOOSE_ONE);
      const sizeField = scope.querySelector('[data-pc-size-option]');
      if (sizeField) sizeField.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return;
    }
    const gaps = missing();
    if (!gaps.length) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    markMissing(gaps);
    showMessage(MISSING);
    gaps[0].size.scrollIntoView({ block: 'center', behavior: 'smooth' });
    gaps[0].size.focus({ preventScroll: true });
  };
  const buyArea = block.closest('.pc-buy') || scope;
  window.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('button, [role="button"], input[type="submit"]');
      if (!button || !buyArea.contains(button) || button.closest('kaching-bundle .kaching-bundles__bar')) return;
      const form = button.form || button.closest('form');
      const isAdd =
        button.matches('[name="add"], .pc-atc, [data-pc-atc], .product-form__submit') ||
        (form && /\/cart\/add/.test(form.getAttribute('action') || '') && button.type === 'submit') ||
        /add to cart/i.test(button.textContent || '');
      if (isAdd) stopIfMissing(event);
    },
    true
  );
  window.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (form instanceof HTMLFormElement && buyArea.contains(form) && /\/cart\/add/.test(form.getAttribute('action') || '')) {
        stopIfMissing(event);
      }
    },
    true
  );

  // Final check on the request itself.
  const readItems = (body) => {
    const items = {};
    const take = (key, value) => {
      const field = /^items\[(\d+)\]\[(id|quantity|parent_id)\]$/.exec(key);
      if (field) {
        (items[field[1]] = items[field[1]] || {})[field[2]] = String(value);
        return;
      }
      const prop = /^items\[(\d+)\]\[properties\]\[__kaching_bundles\]$/.exec(key);
      if (prop) (items[prop[1]] = items[prop[1]] || {}).kaching = String(value);
    };
    if (body instanceof FormData || body instanceof URLSearchParams) {
      for (const [key, value] of body.entries()) take(key, value);
    } else if (typeof body === 'string') {
      try {
        (JSON.parse(body).items || []).forEach((item, i) => {
          items[i] = {
            id: String(item.id),
            quantity: String(item.quantity || 1),
            parent_id: item.parent_id ? String(item.parent_id) : undefined,
            kaching: item.properties && item.properties.__kaching_bundles,
          };
        });
      } catch (e) {
        new URLSearchParams(body).forEach((value, key) => take(key, value));
      }
    }
    return Object.values(items);
  };
  const expectedCounts = () => {
    const counts = {};
    for (const slot of slots()) {
      if (!isChosen(slot)) return null;
      const size = slot.size.value;
      const color = slot.color ? slot.color.value : null;
      const variant = variants.find((v) => v.options.includes(size) && (!color || v.options.includes(color)));
      if (!variant) return null;
      counts[variant.id] = (counts[variant.id] || 0) + 1;
    }
    return counts;
  };

  // Returns a 422 Response to refuse the request, or null to let it through.
  const check = (url, init) => {
    if (!/\/cart\/add(\.js)?(\?|$)/.test(url) || !init || !init.body || !document.contains(block)) return null;
    const main = readItems(init.body).filter((item) => item.kaching && !item.parent_id && variantIds.has(item.id));
    if (!main.length) return null;
    if (slots().length === 0) {
      const picked = themeVariantId();
      if (picked && main.every((item) => item.id === picked)) {
        clearMessage();
        return null;
      }
      const text = picked ? MISMATCH : CHOOSE_ONE;
      showMessage(text);
      return new Response(JSON.stringify({ status: 422, message: 'Size required', description: text }), {
        status: 422,
        headers: { 'Content-Type': 'application/json' },
      });
    }
    const want = expectedCounts();
    const got = {};
    main.forEach((item) => {
      got[item.id] = (got[item.id] || 0) + Number(item.quantity || 1);
    });
    const same =
      !!want &&
      Object.keys(want).length === Object.keys(got).length &&
      Object.keys(want).every((id) => want[id] === got[id]);
    if (same) {
      clearMessage();
      return null;
    }
    const gaps = missing();
    markMissing(gaps);
    const text = gaps.length ? MISSING : MISMATCH;
    showMessage(text);
    return new Response(JSON.stringify({ status: 422, message: 'Size required', description: text }), {
      status: 422,
      headers: { 'Content-Type': 'application/json' },
    });
  };

  if (Array.isArray(window.PrimeCutCartAddGuards)) {
    // layout/theme.liquid wraps fetch before any app does. Kaching adds its bundle items inside its own fetch wrapper,
    // so only that innermost guard sees the request that actually reaches the cart.
    window.PrimeCutCartAddGuards.push(check);
  } else {
    const upstreamFetch = window.fetch;
    window.fetch = function pcKachingSizeFetch(input, init) {
      let url = '';
      try {
        url = typeof input === 'string' ? input : input instanceof URL ? input.href : (input && input.url) || '';
      } catch (e) {
        url = '';
      }
      const blocked = check(url, init);
      return blocked ? Promise.resolve(blocked) : upstreamFetch.apply(window, arguments);
    };
  }
  };

  const ready = document.querySelector('kaching-bundle');
  if (ready) {
    start(ready);
  } else if (document.querySelector('[data-pc-pdp]')) {
    // The Kaching app embed can add its block after this deferred script runs (for example on a fallback product
    // template), so wait for it instead of leaving that block unguarded.
    const observer = new MutationObserver(() => {
      const found = document.querySelector('kaching-bundle');
      if (found) {
        observer.disconnect();
        start(found);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), 30000);
  }
})();
